import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Alert,
  Badge,
  Button,
  Loader,
  Input,
  Select,
  Dialog,
  Title,
  Body,
  Table,
} from '../components/kern';
import type { TableColumn } from '../components/kern';

interface User {
  id: number;
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  is_active: boolean;
  last_login: string | null;
  created_at: string;
  updated_at: string;
}

interface UserFormData {
  email: string;
  name: string;
  role: 'admin' | 'viewer';
  password?: string;
}

export function UsersPage() {
  const { token, user: currentUser, authMode, keycloakAdminUrl } = useAuth();
  const isOidc = authMode === 'oidc';
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null);
  const [formData, setFormData] = useState<UserFormData>({
    email: '',
    name: '',
    role: 'viewer',
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/users', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Fehler beim Laden der Benutzer');
      }

      const data = await response.json();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/api/v1/users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          role: formData.role,
          password: formData.password || undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Erstellen des Benutzers');
      }

      const result = await response.json();

      if (result.temporaryPassword) {
        setTemporaryPassword(result.temporaryPassword);
      }

      await fetchUsers();
      setShowCreateModal(false);
      resetForm();
      setSuccess('Benutzer erfolgreich erstellt');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUser) return;
    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/v1/users/${editingUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          role: formData.role,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Fehler beim Aktualisieren des Benutzers');
      }

      await fetchUsers();
      setEditingUser(null);
      resetForm();
      setSuccess('Benutzer erfolgreich aktualisiert');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (user: User) => {
    if (user.id === currentUser?.id) {
      setError('Sie können sich nicht selbst deaktivieren');
      return;
    }

    setError('');
    setSuccess('');

    try {
      const response = await fetch(`/api/v1/users/${user.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          is_active: !user.is_active,
        }),
      });

      if (!response.ok) {
        throw new Error('Fehler beim Ändern des Status');
      }

      await fetchUsers();
      setSuccess(`Benutzer ${user.is_active ? 'deaktiviert' : 'aktiviert'}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
    }
  };

  // TODO: Wire up to UI when needed
  // const handleResetPassword = async (user: User) => {
  //   if (!confirm(`Möchten Sie das Passwort für ${user.email} wirklich zurücksetzen?`)) {
  //     return;
  //   }

  //   setError('');
  //   setSuccess('');

  //   try {
  //     const response = await fetch(`/api/v1/users/${user.id}/reset-password`, {
  //       method: 'POST',
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error('Fehler beim Zurücksetzen des Passworts');
  //     }

  //     const result = await response.json();
  //     setTemporaryPassword(result.temporaryPassword);
  //     setSuccess(`Passwort für ${user.email} wurde zurückgesetzt`);
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
  //   }
  // };

  // const handleDeleteUser = async (user: User) => {
  //   if (user.id === currentUser?.id) {
  //     setError('Sie können sich nicht selbst löschen');
  //     return;
  //   }

  //   if (!confirm(`Möchten Sie den Benutzer ${user.email} wirklich deaktivieren?`)) {
  //     return;
  //   }

  //   setError('');
  //   setSuccess('');

  //   try {
  //     const response = await fetch(`/api/v1/users/${user.id}`, {
  //       method: 'DELETE',
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //       },
  //     });

  //     if (!response.ok) {
  //       throw new Error('Fehler beim Deaktivieren des Benutzers');
  //     }

  //     await fetchUsers();
  //     setSuccess('Benutzer erfolgreich deaktiviert');
  //   } catch (err) {
  //     setError(err instanceof Error ? err.message : 'Ein Fehler ist aufgetreten');
  //   }
  // };

  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'viewer',
      password: '',
    });
  };

  const openEditModal = (user: User) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      password: '',
    });
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleBadge = (role: string) => {
    const roleMap: Record<
      string,
      { variant: 'info' | 'success' | 'warning' | 'danger' | 'neutral'; label: string }
    > = {
      admin: { variant: 'info', label: 'Administrator' },
      viewer: { variant: 'neutral', label: 'Betrachter' },
    };
    const { variant, label } = roleMap[role] || roleMap.viewer;
    return <Badge variant={variant}>{label}</Badge>;
  };

  const getStatusBadge = (isActive: boolean) => {
    return isActive ? (
      <Badge variant="success">Aktiv</Badge>
    ) : (
      <Badge variant="danger">Inaktiv</Badge>
    );
  };

  const columns: TableColumn<User>[] = [
    {
      key: 'name',
      header: 'Name',
      render: (user) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
          <strong>{user.name}</strong>
          {user.id === currentUser?.id && <Badge variant="info">Sie</Badge>}
        </span>
      ),
    },
    { key: 'email', header: 'E-Mail' },
    { key: 'role', header: 'Rolle', render: (user) => getRoleBadge(user.role) },
    { key: 'is_active', header: 'Status', render: (user) => getStatusBadge(user.is_active) },
    { key: 'last_login', header: 'Letzter Login', render: (user) => formatDate(user.last_login) },
    { key: 'created_at', header: 'Erstellt', render: (user) => formatDate(user.created_at) },
    ...(!isOidc
      ? [
          {
            key: 'actions' as keyof User,
            header: 'Aktionen',
            render: (user: User) => (
              <div className="action-buttons">
                <Button
                  variant="secondary"
                  icon="edit"
                  onClick={() => openEditModal(user)}
                  title="Bearbeiten"
                >
                  Bearbeiten
                </Button>
                {user.id !== currentUser?.id && (
                  <Button
                    variant="secondary"
                    icon={user.is_active ? 'close' : 'check'}
                    onClick={() => handleToggleActive(user)}
                    title={user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  >
                    {user.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </Button>
                )}
              </div>
            ),
          },
        ]
      : []),
  ];

  if (isLoading) {
    return (
      <div className="loading-container">
        <Loader />
        <Body>Lade Benutzer...</Body>
      </div>
    );
  }

  return (
    <div className="page-container">
      <header className="page-header">
        <Title>Benutzerverwaltung</Title>
        {!isOidc && (
          <Button variant="primary" icon="add" onClick={() => setShowCreateModal(true)}>
            Neuer Benutzer
          </Button>
        )}
      </header>

      <div className="kern-container">
        {isOidc && (
          <div className="kern-row">
            <div className="kern-col-12">
              <Alert variant="info" className="page-alert">
                <div>
                  Die Benutzerverwaltung erfolgt über Keycloak. Benutzer und Rollen können nur dort
                  verwaltet werden.
                  {keycloakAdminUrl && (
                    <div style={{ marginTop: '8px' }}>
                      <a
                        href={keycloakAdminUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="kern-btn kern-btn--secondary kern-btn--small"
                      >
                        Keycloak-Benutzerverwaltung öffnen
                      </a>
                    </div>
                  )}
                </div>
              </Alert>
            </div>
          </div>
        )}
        {error && (
          <div className="kern-row">
            <div className="kern-col-12">
              <Alert variant="danger" className="page-alert" onClose={() => setError('')}>
                {error}
              </Alert>
            </div>
          </div>
        )}

        {success && (
          <div className="kern-row">
            <div className="kern-col-12">
              <Alert variant="success" className="page-alert" onClose={() => setSuccess('')}>
                {success}
              </Alert>
            </div>
          </div>
        )}

        {temporaryPassword && (
          <div className="kern-row">
            <div className="kern-col-12">
              <Alert
                variant="warning"
                className="page-alert"
                onClose={() => setTemporaryPassword(null)}
              >
                <div>
                  <strong>Temporäres Passwort:</strong>
                  <code className="temp-password-code">{temporaryPassword}</code>
                  <Body size="small">
                    Bitte notieren Sie dieses Passwort. Es wird nur einmal angezeigt.
                  </Body>
                </div>
              </Alert>
            </div>
          </div>
        )}

        <div className="kern-row">
          <div className="kern-col-12">
            <Table<User>
              columns={columns}
              data={users}
              keyField="id"
              striped
              className="users-table"
              rowClassName={(user) => (!user.is_active ? 'row-inactive' : '')}
            />
          </div>
        </div>
      </div>

      {users.length === 0 && (
        <div className="empty-state">
          <Body>Keine Benutzer vorhanden.</Body>
        </div>
      )}

      {/* Create/Edit Modal - only in local auth mode */}
      {!isOidc && (
        <Dialog
          open={showCreateModal || !!editingUser}
          onClose={() => {
            setShowCreateModal(false);
            setEditingUser(null);
            resetForm();
          }}
          title={editingUser ? 'Benutzer bearbeiten' : 'Neuer Benutzer'}
        >
          <form onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
            <div className="kern-dialog__body">
              <Input
                id="name"
                label="Name"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                minLength={2}
              />

              <Input
                id="email"
                label="E-Mail"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />

              <Select
                id="role"
                label="Rolle"
                required
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value as 'admin' | 'viewer' })
                }
                options={[
                  { value: 'viewer', label: 'Betrachter - Kann Feedbacks nur ansehen' },
                  { value: 'admin', label: 'Administrator - Voller Zugriff' },
                ]}
              />

              {!editingUser && (
                <Input
                  id="password"
                  label="Passwort (optional)"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  minLength={8}
                  placeholder="Mind. 8 Zeichen (leer = automatisch generiert)"
                  hint="Wenn leer, wird ein temporäres Passwort generiert."
                />
              )}
            </div>
            <footer className="kern-dialog__footer">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCreateModal(false);
                  setEditingUser(null);
                  resetForm();
                }}
              >
                Abbrechen
              </Button>
              <Button type="submit" variant="primary" disabled={isSubmitting}>
                {isSubmitting ? 'Speichern...' : editingUser ? 'Aktualisieren' : 'Erstellen'}
              </Button>
            </footer>
          </form>
        </Dialog>
      )}
    </div>
  );
}
